import React, {useState, useEffect, useCallback, useRef} from "react";
import useCountriesData from "../../../../hooks/useContriesData";
import { MAX_BOUNDS, MIN_ZOOM, MAX_ZOOM, DEFAULT_MAP_STATE} from "../../../../config/config";
import { WebMercatorViewport } from "@math.gl/web-mercator";
import useComponentSize from "@rehooks/component-size";
import DeckGL, { GeoJsonLayer} from "deck.gl";
import MapControls from "../map-controls/map-controls";
import MapToolTip from "../map-tooltip/map-tooltip";
import styles from "./map-countries.module.scss";
import { getLonAndLat, hexToRgb, renderHTML, formatRank} from "../../../../utils/utils";
import { mapBgColours } from "../../../../config/project-config";


const MapCountries = ({
  listOfCountries,
  selectedCode,
  externalItemHovered,
  currentMetric = 'totalScore',
  // embedType
  // onHover
}) => {
  const [hoveredCountry, setHoveredCountry] = useState(null);
  // const [selectedCountry, setSelectedCountry] = useState(null);
  const [allCountriesData, setAllCountriesData] = useState(null);

  const [selectedCountries, setSelectedCountries] = useState([]);
  const [unselectedCountries, setUnselectedCountries] = useState([]);
  const [minZoom, setMinZoom] = useState(MIN_ZOOM);
  const [viewport, setViewport] = useState(DEFAULT_MAP_STATE);
  let mapRef = useRef(null);
  // let listRef = useRef(null);
  let { width, height } = useComponentSize(mapRef);

  const countriesData = useCountriesData();
  const popUpRef = useRef(null);

  useEffect(() => {
    // Small timeout to ensure component is fully rendered
    const timeoutId = setTimeout(() => {
      // Use actual dimensions or fallback to reasonable defaults
      const mapWidth = width || 800;
      const mapHeight = height || 600;

      try {
        const newViewport = new WebMercatorViewport({
          width: mapWidth,
          height: mapHeight,
          maxZoom: MAX_ZOOM,
          minZoom: MIN_ZOOM,
          ...DEFAULT_MAP_STATE
        }).fitBounds(MAX_BOUNDS);

        setMinZoom(newViewport.zoom);
        setViewport(newViewport);
      } catch (error) {
        console.error('Error initializing viewport:', error);
        // Fallback to safe defaults
        setViewport({
          ...DEFAULT_MAP_STATE,
          width: mapWidth,
          height: mapHeight,
          zoom: 1
        });
      }
    }, 100); // 100ms delay

    // Cleanup timeout if component unmounts or dependencies change
    return () => clearTimeout(timeoutId);
  }, [width, height]);

  const handleViewStateChange = useCallback((newState) => {
    if (newState.oldViewState !== null) {
      // clamp the zoom
      newState.viewState.zoom =
        newState.viewState.zoom > MAX_ZOOM ? MAX_ZOOM : newState.viewState.zoom;
      newState.viewState.zoom =
        newState.viewState.zoom < MIN_ZOOM ? MIN_ZOOM : newState.viewState.zoom;
      setViewport((v) =>
        v
          ? new WebMercatorViewport({
              ...v,
              ...newState.viewState,
            })
          : null
      );
    }
  }, []);

  const resetZoom = useCallback(() => {
    try {
      const newViewport = new WebMercatorViewport({
        width: width || 800,
        height: height || 600,
        maxZoom: MAX_ZOOM,
        minZoom: MIN_ZOOM,
        zoom: 0,
        longitude: 0,
        latitude: 0,
      }).fitBounds(MAX_BOUNDS);

      setViewport(newViewport);
    } catch (error) {
      console.error('Error resetting zoom:', error);
    }
  }, [width, height]);

  const zoomIncrement = useCallback(
    (amt) => {
      const newZoom = Math.max(
        Math.min(Math.max(viewport.zoom + amt, 0.001), MAX_ZOOM),
        minZoom
      );
      handleViewStateChange({
        viewState: {
          zoom: newZoom,
          transitionDuration: 5000,
        },
      });
    },
    [viewport, handleViewStateChange, minZoom]
  );



  useEffect(() => {

    const showVisibleCountries = (includeCountries, features) =>{
      const filteredFeatures = features.filter((feature) =>
        includeCountries.includes(feature.properties.ISO2)
      );

      const visibleCountries = {
        type: "FeatureCollection",
        features: filteredFeatures,
      };
      setSelectedCountries(visibleCountries);
    }

    const hideNotVisibleCountries = (includeCountries, features) =>{
        const unfilteredFeatures = features.filter(
          (feature) => !includeCountries.includes(feature.properties.ISO2)
        );
        const notVisibleCountries = {
          type: "FeatureCollection",
          features: unfilteredFeatures,
        };
        setUnselectedCountries(notVisibleCountries);
    }



    if(countriesData){
       
        let convertedListOfCountries = Object.values(listOfCountries);
        // console.log(convertedListOfCountries);
        let features = countriesData.features; 
      

        let filteredData = [];
        let countriesCode = [];

        for(let i = 0; i < convertedListOfCountries.length; i++){

          // let obj = {}
          if(getLonAndLat(convertedListOfCountries[i].code, features)){
            let obj = {}
            // let countryNameFromList = getCountryNameByISO3(listOfCountries[i]);
            // let countryNameFromMapData = getLonAndLat(listOfCountries[i], features).country;
            obj['countryCode'] = convertedListOfCountries[i].code;
            // obj['country'] = countryNameFromList ? countryNameFromList : countryNameFromMapData;
            obj['country'] = convertedListOfCountries[i].name;
            obj['longitude'] = getLonAndLat(convertedListOfCountries[i].code, features).longitude;
            obj['latitude'] = getLonAndLat(convertedListOfCountries[i].code, features).latitude;
            obj['type'] = convertedListOfCountries[i].type;
            obj['note'] = convertedListOfCountries[i].note;
            filteredData.push(obj);
            countriesCode.push(convertedListOfCountries[i].code);
          }else{
            console.log(convertedListOfCountries[i].code + ' ' + convertedListOfCountries[i].name + ' is not found');
          }

        }

        const sortedData = filteredData.sort((a, b) => 
            a.country.localeCompare(b.country)
        );

        // console.log(sortedData);
   
        setAllCountriesData(sortedData);
        showVisibleCountries(countriesCode, features);
        hideNotVisibleCountries(countriesCode, features);

        // console.log(filteredData);

    }
  },[countriesData, listOfCountries]);

  useEffect(() => {
    setHoveredCountry(externalItemHovered ? externalItemHovered : null);
  },[externalItemHovered])

  const getProjectionCoordinates = (coords) => {
    if (viewport && viewport.project && typeof viewport.zoom === 'number') {
      try {
        return viewport.project([coords[0], coords[1]]);
      } catch (error) {
        console.warn('Projection error:', error);
        return [0, 0];
      }
    }
    return [0, 0];
  };

  const getFillColor = (d) =>{
    const targetData = allCountriesData.filter(item => item.countryCode === d.properties.ISO2);
    if (targetData.length === 0 || !targetData[0] || !targetData[0].type) {
      // Return default gray color for countries not in our data
      return [200, 200, 200]; // Light gray
    }
    const targetHexColor = mapBgColours[targetData[0].type];
    return hexToRgb(targetHexColor);
  }


  const handleHover = (data, fromMap = false) => {

    
   
    if (fromMap) {
      // if(data.object){
      //   const element = document.getElementById(`list-item-${slugify(getCountryNameByISO3(data.object.properties.ISO3))}`);
      //   listRef.current.scrollTo(0, element.offsetTop - 30);
      //   console.log(data.object.properties);
      // }
      setHoveredCountry(data.object ? data.object.properties.ISO2 : null);
      // onHover(data, fromMap);
      
    }else{
      // setHoveredCountry(data !== null ? data.country : null);
    }
  };

  // const handleClick = (data) =>{
  //   // setSelectedCountry(data.country);
  //   // popUpRef.current.scrollIntoView({ behavior: "smooth" });
  //   // handleViewStateChange({
  //   //   viewState: {
  //   //     zoom: 2,
  //   //     longitude: data.longitude,
  //   //     latitude: data.latitude,
  //   //     transitionDuration: 5000,
  //   //   },
  //   // });
  // }



  return (

    <div className="innerContainer">



        <div className="row">
          <div className="col-12">
            <div className={styles.mapColumn}>
            
                <div className={styles.mapWrapper} ref={mapRef}>
                  <DeckGL
                    viewState={viewport}
                    controller={{scrollZoom:false, doubleClickZoom: false}}
                    onViewStateChange={handleViewStateChange}
                  >
                    <GeoJsonLayer
                      id="map-unselected-countries"
                      data={unselectedCountries ? unselectedCountries : null}
                      getLineColor={[255, 255, 255]}
                      getFillColor={[233, 233, 233]}
                      lineWidthUnits={"pixels"}
                      stroked={true}
                    />
                    <GeoJsonLayer
                      id="map-selected-countries"
                      data={selectedCountries ? selectedCountries : null}
                      getLineColor={[255, 255, 255]}
                      // getFillColor={[64, 83, 99]}
                      getFillColor = { d => getFillColor(d)}
                      lineWidthUnits={"pixels"}
                      stroked={true}
                      pickable={true}
                      pickingRadius={10}
                      onHover={(info) => handleHover(info, true)}
                    />
                  </DeckGL>

                  {allCountriesData &&
                    allCountriesData.map((d, index) => {
                      return (
                        <React.Fragment key={d.countryCode + index + "-tooltip"}>
                          {(hoveredCountry === d.countryCode || selectedCode === d.countryCode) && (
                            <MapToolTip
                              top={getProjectionCoordinates([d.longitude,d.latitude,])[1]-2}
                              left={getProjectionCoordinates([d.longitude,d.latitude,])[0]}
                              currentMetric={currentMetric}
                            >
                              <div className={styles.tooltipCountryName}>{d.country}</div>
                              {d.note && typeof d.note === 'object' ? (
                                <div className={styles.tooltipNote}>
                                  <div className={styles.tooltipRow}>
                                    <span className={styles.tooltipLabel}>Global Investment Risk and Resilience Index Rank</span>
                                    <span className={styles.tooltipValue}>{formatRank(d.note.rank)}</span>
                                  </div>
                                  {currentMetric === 'totalScore' && (
                                    <>
                                      <div className={styles.tooltipRow}>
                                        <span className={styles.tooltipLabel}>Total Global Investment Risk and Resilience Index Score</span>
                                        <span className={styles.tooltipValue}>{d.note.totalScore}</span>
                                      </div>
                                      <div className={styles.tooltipRow}>
                                        <span className={styles.tooltipLabel}>Investment Category</span>
                                        <span className={styles.tooltipValue}>{d.note.scoreCategory}</span>
                                      </div>
                                    </>
                                  )}
                                  {currentMetric === 'riskScore' && (
                                    <>
                                      <div className={styles.tooltipRow}>
                                        <span className={styles.tooltipLabel}>Total Risk Score</span>
                                        <span className={styles.tooltipValue}>{d.note.riskScore}</span>
                                      </div>
                                      <div className={styles.tooltipRow}>
                                        <span className={styles.tooltipLabel}>Risk Category</span>
                                        <span className={styles.tooltipValue}>{d.note.riskCategory}</span>
                                      </div>
                                    </>
                                  )}
                                  {currentMetric === 'resilienceScore' && (
                                    <>
                                      <div className={styles.tooltipRow}>
                                        <span className={styles.tooltipLabel}>Total Resilience Score</span>
                                        <span className={styles.tooltipValue}>{d.note.resilienceScore}</span>
                                      </div>
                                      <div className={styles.tooltipRow}>
                                        <span className={styles.tooltipLabel}>Resilience Category</span>
                                        <span className={styles.tooltipValue}>{d.note.resilienceCategory}</span>
                                      </div>
                                    </>
                                  )}
                                </div>
                              ) : (
                                <div className={styles.tooltipNote} style={{color:'#405363'}}>
                                  {renderHTML(d.note)}
                                </div>
                              )}
                            </MapToolTip>
                          )}
                        </React.Fragment>
                      );
                    })}
                    <MapControls
                      zoomIncrement={() => zoomIncrement(0.5)}
                      zoomDecrement={() => zoomIncrement(-0.5)}
                      resetZoom={() => resetZoom()}
                    /> 
                </div>
            
                <div ref={popUpRef} className={styles.positionToScroll}></div>
            </div>
            
          </div>
         
        </div>

    </div>

  );
}

export default MapCountries;